/**
 * Converts the page markers of a flat stream of book pages into the page-number elements that the book styles and the
 * navigation of the book renderer already know. A marker is an empty element that starts a page:
 * <anchor pageNumber="N"></anchor>, or, when the content is obfuscated, <t class="... anchor" pageNumber="N"></t>.
 * Content that is wrapped in <page> elements (which already hold a page-number element) has no markers and stays as it is.
 */
export class BookPageMarkers {

  /** An opening tag of a candidate marker, optionally self-closed, with the closing tag that directly follows it. */
  private static readonly CANDIDATE = /<(anchor|t)(\s[^<>]*?)?\s*(\/>|>(?:\s*<\/\1\s*>)?)/gi;
  private static readonly PAGE_NUMBER = /\spagenumber\s*=\s*(?:"([^"]*)"|'([^']*)')/i;
  private static readonly CLASS = /\sclass\s*=\s*(?:"([^"]*)"|'([^']*)')/i;

  /** Replaces every marker with <page-number class="page-marker">N</page-number>. */
  convert(html: string): string {
    if (!html || html.toLowerCase().indexOf('pagenumber') < 0) {
      return html;
    }
    return html.replace(BookPageMarkers.CANDIDATE, (match: string, tag: string, attributes: string) => {
      const pageNumber = this.pageNumberOf(tag.toLowerCase(), attributes || '');
      return pageNumber == null ? match : `<page-number class="page-marker">${pageNumber}</page-number>`;
    });
  }

  private pageNumberOf(tag: string, attributes: string): string | null {
    if (tag === 't' && !this.hasAnchorClass(attributes)) {
      return null;
    }
    const found = BookPageMarkers.PAGE_NUMBER.exec(attributes);
    const value = found ? (found[1] ?? found[2] ?? '').trim() : '';
    return /^\d+$/.test(value) ? value : null;
  }

  private hasAnchorClass(attributes: string): boolean {
    const found = BookPageMarkers.CLASS.exec(attributes);
    const classes = found ? (found[1] ?? found[2] ?? '') : '';
    return classes.split(/\s+/).indexOf('anchor') >= 0;
  }
}
